import type { DealTroubleshoot } from '@openx/types/deal.ts';
import type { TroubleshootingFunnelApi } from '@openx/types/troubleshootingFunnel.ts';
import { formatISO } from 'date-fns';
import type { TFunction } from 'i18next';
import { unparse } from 'papaparse';

export const convertTroubleshootingMetricsToCsv = (data: DealTroubleshoot[], t?: TFunction): string => {
  if (!data || data.length === 0) {
    return '';
  }

  const rows = data.map(row => {
    return {
      // We want to show timestamp in the CSV in the user's timezone
      timestamp: formatISO(row.timestamp),
      bid_requests: row.stats.bid_requests,
      bid_responses: row.stats.bid_responses,
      impressions: row.stats.impressions,
      bid_cpm:
        row.stats.bid_cpm !== undefined && row.stats.bid_cpm !== null ? Number(row.stats.bid_cpm.toFixed(2)) : '',
    };
  });

  const csv = unparse(rows, {
    columns: ['timestamp', 'bid_requests', 'bid_responses', 'impressions', 'bid_cpm'],
  });

  // If translation function is provided, replace header row with translated headers
  if (t) {
    const lines = csv.split('\r\n');
    const translatedHeader = [
      t('Timestamp'),
      t('Bid Requests'),
      t('Bid Responses'),
      t('Impressions'),
      t('Bid CPM'),
    ].join(',');
    lines[0] = translatedHeader;
    return lines.join('\r\n');
  }

  return csv;
};

/**
 * Converts troubleshooting funnel data to CSV format
 */
export const convertTroubleshootingFunnelToCsv = (
  data: TroubleshootingFunnelApi,
  startDate?: string,
  endDate?: string,
  t?: TFunction
): string => {
  const dateRange = startDate && endDate ? `${startDate} - ${endDate}` : '';

  // Format upshot codes with pipe separator (AC criteria)
  // Multiply percentage by 100 as API returns fractions
  let upshotCodesFormatted = '';
  if (data.upshot_codes) {
    const upshotEntries = Object.entries(data.upshot_codes)
      .map(([code, value]) => `${code}: ${value.count} (${(value.percentage * 100).toFixed(2)}%)`)
      .join('|');
    upshotCodesFormatted = upshotEntries;
  }

  // Multiply percentage values by 100 as API returns fractions
  const row = {
    date_range: dateRange,
    health_score: data.health_score ?? '',
    spend: data.spend ?? '',
    cpm: data.cpm ?? '',
    viewability: data.viewability !== null ? data.viewability * 100 : '',
    ctr: data.ctr !== null ? data.ctr * 100 : '',
    vtr: data.vtr !== null ? data.vtr * 100 : '',
    impressions: data.impressions ?? '',
    eligible_bid_requests: data.eligible_bid_requests ?? '',
    targeted_bid_requests: data.targeted_bid_requests ?? '',
    bid_responses: data.bid_responses ?? '',
    fills: data.fills ?? '',
    upshot_codes: upshotCodesFormatted,
  };

  const csv = unparse([row], {
    columns: [
      'date_range',
      'health_score',
      'spend',
      'cpm',
      'viewability',
      'ctr',
      'vtr',
      'impressions',
      'eligible_bid_requests',
      'targeted_bid_requests',
      'bid_responses',
      'fills',
      'upshot_codes',
    ],
  });

  // If translation function is provided, replace header row with translated headers
  if (t) {
    const lines = csv.split('\r\n');
    const translatedHeader = [
      t('Date Range'),
      t('Health Score'),
      t('Spend'),
      t('CPM'),
      t('Viewability'),
      t('CTR'),
      t('VTR'),
      t('Impressions'),
      t('Total Eligible Requests'),
      t('Eligible Targeted Requests'),
      t('Bid Responses'),
      t('Fills'),
      t('Upshot Codes'),
    ].join(',');
    lines[0] = translatedHeader;
    return lines.join('\r\n');
  }

  return csv;
};

export const downloadCsv = (csvContent: string, filename: string): void => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};
